import type React from 'react'
import { useState, useRef, useEffect } from 'react'
import { ChevronDownIcon } from 'lucide-react'

interface VltTypeStepProps {
  value: number
  onChange: (vltType: number | null) => void
}

const vltTypeOptions = [
  { value: 0, label: '선택하세요', disabled: true },
  { value: 1, label: '1타입' },
  { value: 2, label: '2타입' },
  { value: 3, label: '3타입' },
  { value: 4, label: '4타입' },
  { value: 5, label: '5타입' },
  { value: 6, label: '6타입' },
  { value: 7, label: '7타입' },
  { value: 8, label: '8타입' },
  { value: 9, label: '9타입' },
  { value: 10, label: '10타입' },
  { value: 11, label: '11타입' },
  { value: 12, label: '12타입' },
  { value: 13, label: '13타입' },
  { value: 14, label: '14타입' },
  { value: 15, label: '15타입' },
  { value: 16, label: '16타입' }
]

export function VltTypeStep({ value, onChange }: VltTypeStepProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const selectRef = useRef<HTMLDivElement>(null)

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // 키보드 이벤트 처리
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (selectedIndex >= 0 && !vltTypeOptions[selectedIndex].disabled) {
        selectOption(vltTypeOptions[selectedIndex])
      } else {
        setIsOpen(!isOpen)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (isOpen) {
        const nextIndex = selectedIndex < vltTypeOptions.length - 1 ? selectedIndex + 1 : 0
        setSelectedIndex(nextIndex)
      } else {
        setIsOpen(true)
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (isOpen) {
        const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : vltTypeOptions.length - 1
        setSelectedIndex(prevIndex)
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setSelectedIndex(-1)
    }
  }

  // 옵션 선택
  const selectOption = (option: (typeof vltTypeOptions)[0]) => {
    if (!option.disabled) {
      onChange(option.value)
      setIsOpen(false)
      setSelectedIndex(-1)
    }
  }

  // 현재 선택된 옵션 찾기
  const selectedOption = vltTypeOptions.find(option => option.value === value)

  return (
    <div className='space-y-4'>
      <div className='space-y-2 mb-6 text-center'>
        <h2 className='text-[24px] font-[700] text-[#020202]'>
          VLT 검사 타입은 뭐야?
          <br />
          (모를 땐 매니저님께 문의!)
        </h2>
      </div>
      <div className='relative'>
        <div
          ref={selectRef}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          className='w-full px-6 py-3 rounded-2xl bg-white/70 backdrop-blur-sm border-2 border-[#FFA236]/20 focus:border-[#FFA236] focus:outline-none focus:ring-2 focus:ring-[#FFA236]/50 transition-all duration-300 text-center text-[18px] font-[500] cursor-pointer flex items-center justify-between'
        >
          <span className={`flex-1 ${!selectedOption || selectedOption.disabled ? 'text-gray-400' : 'text-gray-800'}`}>
            {selectedOption ? selectedOption.label : '선택하세요'}
          </span>
          <ChevronDownIcon
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>

        {/* 드롭다운 리스트 */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto'
          >
            {vltTypeOptions.slice(1).map((option, index) => (
              <div
                key={option.value}
                onClick={() => selectOption(option)}
                className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                  selectedIndex === index + 1 ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                } ${option.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <div className='font-medium text-center'>{option.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
